"use client";

import NextLink from "next/link";
import { useState, useSyncExternalStore } from "react";

import { useHelpDialog } from "@/components/homepage/help-modal";

import { TbMonogram } from "@/components/brand/tb-monogram";
import { TbWordmark } from "@/components/brand/tb-wordmark";
import { PRIVACY_HREF, TERMS_HREF } from "@/lib/legal/legal-document";

const FOOTER_COLUMNS = [
  {
    title: "A Lista",
    links: [
      { href: "/listas", label: "Encontre uma lista" },
      { href: "/onboarding", label: "Crie sua lista" },
      { href: "#listas", label: "Listas Modelo" },
    ],
  },
  {
    title: "Central de Informações",
    links: [
      { href: TERMS_HREF, label: "Termos e Condições" },
      { href: PRIVACY_HREF, label: "Política de Privacidade" },
    ],
  },
  // "Institucional", "Perguntas frequentes" and "Trocas" left on 2026-09-29, by the board's
  // ruling: those pages do not exist in the MVP, and a footer link must lead somewhere.
  {
    title: "Atendimento",
    links: [
      // Opens the support channels where they are, as the reference does (`openHelp`).
      { href: "#ajuda", label: "Precisa de Ajuda?", help: true },
      { href: "#ajuda", label: "Atendimento da Concierge" },
    ],
  },
] as const;

/** The brand's own profiles, in the order the board gave them (2026-09-29). */
const SOCIAL_LINKS = [
  { href: "https://www.instagram.com/taniabulhoes/", label: "Instagram" },
  { href: "https://www.tiktok.com/@taniabulhoesoficial", label: "TikTok" },
  { href: "https://www.facebook.com/TaniaBulhoesOficial", label: "Facebook" },
  { href: "https://br.pinterest.com/taniabulhoes/", label: "Pinterest" },
  { href: "https://www.youtube.com/c/taniabulhoes", label: "YouTube" },
] as const;

/**
 * The footer's ink: brand white on the green band, and a hover that underlines instead of
 * changing colour (the board's ruling of 2026-09-29 — green on green read as nothing).
 */
const FOOTER_LINK_INTERACTION =
  "underline-offset-[3px] decoration-1 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent";

type FooterColumn = (typeof FOOTER_COLUMNS)[number];
type FooterLinkItem = FooterColumn["links"][number];

/**
 * The reference draws two footers, not one styled two ways: below 900px the columns are an
 * accordion, all closed, one open at a time; at 900px and above they are open columns under
 * their headings (`vw < 900` in the reference's own logic).
 *
 * Rendering both and hiding one would put every footer link in the page twice — for a screen
 * reader and for the tab order as much as for the markup — so the branch is taken here, the
 * same narrow exception `use-is-wide-viewport.ts` states. The server has no width and renders
 * the accordion, which is correct at any width and sits far below the fold.
 */
const WIDE_FOOTER_QUERY = "(min-width: 900px)";

function subscribeToWidth(onChange: () => void) {
  const mediaQuery = window.matchMedia(WIDE_FOOTER_QUERY);
  mediaQuery.addEventListener("change", onChange);

  return () => mediaQuery.removeEventListener("change", onChange);
}

function useIsWideFooter(): boolean {
  return useSyncExternalStore(
    subscribeToWidth,
    () => window.matchMedia(WIDE_FOOTER_QUERY).matches,
    () => false,
  );
}

/** One link of a column, the same on both sides of the breakpoint. */
function FooterLink({
  link,
  onHelp,
  sectionHrefPrefix,
}: {
  link: FooterLinkItem;
  onHelp: (() => void) | null;
  sectionHrefPrefix: string;
}) {
  const className = `block py-[11px] text-[14px] text-tb-white ${FOOTER_LINK_INTERACTION} [@media(min-width:900px)]:py-0`;

  if ("external" in link && link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={className}>
        {link.label}
      </a>
    );
  }

  return (
    <NextLink
      // A homepage section (`#ajuda`, `#listas`) is reached from another page through the homepage.
      href={link.href.startsWith("#") ? `${sectionHrefPrefix}${link.href}` : link.href}
      onClick={(event) => {
        // Without the dialog around it the link still works: it goes to the section it names.
        if (!("help" in link && link.help) || onHelp === null) return;
        event.preventDefault();
        onHelp();
      }}
      className={className}
    >
      {link.label}
    </NextLink>
  );
}

/** The accordion the reference draws below 900px: one column open at a time, none at rest. */
function FooterAccordion({ onHelp, sectionHrefPrefix }: { onHelp: (() => void) | null; sectionHrefPrefix: string }) {
  const [openColumn, setOpenColumn] = useState<number>(-1);

  return (
    <div className="border-t border-[rgba(237,231,219,0.12)] pt-[clamp(40px,4.5vw,64px)]">
      {FOOTER_COLUMNS.map((column, index) => {
        const isOpen = openColumn === index;
        const panelId = `tb-footer-col-${index}`;

        return (
          <div key={column.title} className="border-b border-[rgba(237,231,219,0.16)]">
            {/* Level 3 for the same reason as the open columns below: the last heading before
                the footer is the newsletter's h2, and skipping a level breaks the outline. */}
            <h3 className="m-0">
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenColumn(isOpen ? -1 : index)}
                className="flex min-h-[52px] w-full cursor-pointer items-center justify-between gap-4 border-none bg-transparent px-[2px] py-4 text-left text-[13px] font-medium uppercase tracking-[0.08em] text-tb-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              >
                <span>{column.title}</span>
                {/* The reference's plus, which loses its upright stroke when the row opens. */}
                <span aria-hidden="true" className="relative block size-[13px] shrink-0">
                  <span className="absolute left-0 top-[6px] block h-[1.5px] w-[13px] bg-tb-white" />
                  <span
                    className="absolute left-[6px] top-0 block h-[13px] w-[1.5px] bg-tb-white transition-[transform,opacity] duration-[220ms] ease-[var(--tb-ease-linen)]"
                    style={{ transform: isOpen ? "scaleY(0)" : "scaleY(1)", opacity: isOpen ? 0 : 1 }}
                  />
                </span>
              </button>
            </h3>

            {/* Clipped, not removed, so the panel slides — and `inert` while closed, so what is
                out of sight is also out of the reading order and out of find-in-page. */}
            <div
              id={panelId}
              inert={!isOpen}
              className="overflow-hidden transition-[max-height] duration-[360ms] ease-[var(--tb-ease-linen)]"
              style={{ maxHeight: isOpen ? 230 : 0 }}
            >
              <ul className="flex flex-col gap-0 px-[2px] pb-[14px]">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <FooterLink link={link} onHelp={onHelp} sectionHrefPrefix={sectionHrefPrefix} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Institutional footer — wordmark, tagline, three link columns, socials, legal.
 *
 * Carries no background of its own: it renders inside `NewsletterFooterBand`,
 * which owns the green fill it shares with the newsletter.
 */
export interface HomepageFooterProps {
  /**
   * Prefix for the homepage's own sections, as the header takes it: empty on the homepage, "/" on
   * any other page that wears this footer (the legal pages), so `#ajuda` still lands on the section.
   */
  sectionHrefPrefix?: string;
}

export function HomepageFooter({ sectionHrefPrefix = "" }: HomepageFooterProps = {}) {
  const help = useHelpDialog();
  const isWide = useIsWideFooter();
  const onHelp = help === null ? null : () => help.open("footer");

  return (
    <footer
      id="footer"
      className="relative z-[1] pb-9 pt-[clamp(90px,calc(20vh-60px),200px)]"
    >
      {/* Crest watermark — decorative, so `TbMonogram` is `aria-hidden`. */}
      <TbMonogram className="pointer-events-none absolute -bottom-[24%] -right-[6%] aspect-[654.87/592.36] w-[clamp(320px,40vw,620px)] text-tb-white opacity-[0.055]" />

      <div className="relative z-[1] px-[clamp(20px,4vw,56px)]">
        <div className="grid grid-cols-1 items-end gap-[clamp(32px,5vw,80px)] pb-[clamp(108px,calc(6vw+60px),156px)] [@media(min-width:900px)]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* Below 900px the brand block is centred, wordmark and signature alike (the reference's
              `brandItems` / `brandTagAlign` / `brandTagText`). */}
          <div className="flex flex-col items-center gap-[clamp(24px,2.8vw,40px)] min-[900px]:items-start">
            {/* Invisible square the reference keeps above the wordmark; it reserves
                the crest's footprint so the brand block's height stays stable. */}
            <span aria-hidden="true" className="block aspect-square w-[clamp(88px,10vw,148px)] invisible" />
            <TbWordmark className="w-[min(100%,clamp(208px,31.2vw,457px))] text-tb-white" />
          </div>
          <p className="max-w-[400px] justify-self-center text-center text-[15px] leading-[1.7] text-tb-white min-[900px]:justify-self-end min-[900px]:text-left [text-wrap:pretty]">
            Listas de casamento e presentes que traduzem afeto. Uma curadoria de peças autorais para vestir a sua nova casa.
          </p>
        </div>

        {isWide ? (
          <div className="tb-footer-cols gap-[clamp(32px,3.5vw,56px)] pt-[clamp(40px,4.5vw,64px)]">
            {FOOTER_COLUMNS.map((column, index) => (
              /* Every column but the first is indented, and only once the row is a
                 single 3-up line (>=1080px). */
              <div key={column.title} className={index > 0 ? "[@media(min-width:1080px)]:pl-[clamp(24px,2.6vw,44px)]" : undefined}>
                {/* Level 3, not 4: the last heading before the footer is the
                    newsletter's h2, and skipping a level breaks the outline. */}
                <h3 className="mb-[18px] text-[12px] font-normal uppercase tracking-[0.18em] text-tb-white">
                  {column.title}
                </h3>
                <ul className="flex flex-col gap-3">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      <FooterLink link={link} onHelp={onHelp} sectionHrefPrefix={sectionHrefPrefix} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <FooterAccordion onHelp={onHelp} sectionHrefPrefix={sectionHrefPrefix} />
        )}

        <div className="mt-[clamp(96px,10vw,150px)] flex flex-wrap items-center justify-center gap-[22px] pb-[26px]">
          {SOCIAL_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-block py-[11px] text-[13px] text-tb-white ${FOOTER_LINK_INTERACTION} [@media(min-width:900px)]:py-0`}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 border-t border-[rgba(237,231,219,0.14)] pt-[26px]">
          <span className="text-center text-[12.5px] leading-[1.65] tracking-[0.02em] text-tb-white [text-wrap:pretty]">
            © {new Date().getFullYear()} Tania Bulhões. Todos os direitos reservados. TB COMÉRCIO DE PRESENTES S/A,
            Avenida José Cesar de Oliveira, Vila Leopoldina, SP, CNPJ 08.613.254/0023-00, IE 129.653.990.118. E-mail:{" "}
            <a
              href="mailto:atendimento@taniabulhoes.com.br"
              className={`text-tb-white underline ${FOOTER_LINK_INTERACTION}`}
            >
              atendimento@taniabulhoes.com.br
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
